import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Search, Globe, MapPin, Info, ChevronRight } from 'lucide-react';
import Layout from './Layout';
import PageHero from './PageHero';

const HowToUsePage = () => {
  const [activeStep, setActiveStep] = useState(0);

  const steps = [
    {
      id: 1,
      title: 'Enter Your Keyword',
      description: 'Type the search term you want to check',
      icon: Search,
      color: 'blue',
      details: [
        'Type any keyword — Google Suggest autocompletes as you type',
        'Use the arrow keys to browse suggestions, Enter to pick one',
        'This is the query whose local rankings you want to see'
      ]
    },
    {
      id: 2,
      title: 'Choose Country, Language & City',
      description: 'Set the location the SERP should come from',
      icon: Globe,
      color: 'green',
      details: [
        'Pick the country — this localizes the Google results',
        'Pick the host language for the results and suggestions',
        'Optionally add a city or area for a precise local SERP — it is located automatically when you search'
      ]
    },
    {
      id: 3,
      title: 'Search',
      description: 'Open the live localized Google results',
      icon: MapPin,
      color: 'purple',
      details: [
        'Click Search to open the live Google SERP for that location in a new tab',
        'The results open on google.com — close to the page a signed-out local user gets',
        'No VPN or proxy needed — the location is sent to Google via URL parameters'
      ]
    }
  ];

  return (
    <Layout breadcrumbItems={[
      { label: 'How to Use', href: '/how-to-use/' }
    ]}>
      <Helmet defer={false}>
        <title>How to Use Free Google SERP Checker - Step by Step Guide</title>
        <meta name="description" content="Learn how to use our free Google SERP checker tool step by step. Check search results from any location worldwide without VPNs. Perfect for SEO professionals." />
        <meta name="keywords" content="how to use serp checker, serp checker tutorial, google serp checker guide, serp checker instructions, serp checker steps, serp checker usage, serp checker tutorial 2024, how to check google rankings, local serp checker guide" />
        <link rel="canonical" href="https://freeserpchecker.org/how-to-use/" />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://freeserpchecker.org/how-to-use/" />
        <meta property="og:title" content="How to Use Free Google SERP Checker - Step by Step Guide" />
        <meta property="og:description" content="Learn how to use our free Google SERP checker tool step by step. Check search results from any location worldwide without VPNs." />
        <meta property="og:image" content="https://freeserpchecker.org/og-image.png" />
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://freeserpchecker.org/how-to-use/" />
        <meta property="twitter:title" content="How to Use Free Google SERP Checker - Step by Step Guide" />
        <meta property="twitter:description" content="Learn how to use our free Google SERP checker tool step by step. Check search results from any location worldwide without VPNs." />
        <meta property="twitter:image" content="https://freeserpchecker.org/og-image.png" />
      </Helmet>

      <section id="how-to-use" className="section-padding bg-white">
        <div className="container-max">
          <div className="max-w-6xl mx-auto">
            <PageHero
              title="How to use the SERP checker"
              subtitle="Check Google search results from any location worldwide in three steps — no VPN, no proxy."
            />

            {/* Step Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
              {steps.map((step, index) => {
                const IconComponent = step.icon;
                const colorClasses = {
                  blue: 'bg-blue-50 border-blue-200 text-blue-700',
                  green: 'bg-green-50 border-green-200 text-green-700',
                  purple: 'bg-purple-50 border-purple-200 text-purple-700',
                  orange: 'bg-orange-50 border-orange-200 text-orange-700',
                  red: 'bg-red-50 border-red-200 text-red-700'
                };

                return (
                  <div
                    key={step.id}
                    className={`card p-6 cursor-pointer transition-all duration-200 hover:shadow-lg ${
                      activeStep === index ? 'ring-2 ring-blue-500' : ''
                    }`}
                    onClick={() => setActiveStep(index)}
                  >
                    <div className="flex items-start space-x-4">
                      <div className={`p-3 rounded-lg ${colorClasses[step.color]}`}>
                        <IconComponent className="w-6 h-6" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <span className="text-sm font-medium text-gray-500">Step {step.id}</span>
                          <ChevronRight className="w-4 h-4 text-gray-400" />
                        </div>
                        <h3 className="font-semibold text-gray-900 mb-1">{step.title}</h3>
                        <p className="text-sm text-gray-600">{step.description}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Detailed Instructions */}
            <div className="bg-gray-50 rounded-2xl p-8">
              <div className="flex items-center space-x-3 mb-6">
                <Info className="w-6 h-6 text-blue-600" />
                <h3 className="text-xl font-semibold text-gray-900">Detailed Instructions</h3>
              </div>

              <div className="space-y-8">
                {/* Step 1 Details */}
                <div className="border-l-4 border-blue-500 pl-6">
                  <h4 className="text-xl font-semibold text-gray-900 mb-3">Step 1: Enter your keyword</h4>
                  <p className="text-gray-600 mb-4">
                    Start with the search term whose local rankings you want to see. Google Suggest autocompletes as you type.
                  </p>
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h5 className="font-medium text-blue-900 mb-2">Tips:</h5>
                    <ul className="space-y-1 text-blue-800">
                      <li>• Real-time Google Suggest suggestions appear as you type</li>
                      <li>• Use the arrow keys to navigate suggestions, Enter to select</li>
                      <li>• Press Enter or click Search to run the query</li>
                    </ul>
                  </div>
                </div>

                {/* Step 2 Details */}
                <div className="border-l-4 border-green-500 pl-6">
                  <h4 className="text-xl font-semibold text-gray-900 mb-3">Step 2: Choose country, language & (optional) city</h4>
                  <p className="text-gray-600 mb-4">
                    The country is the main location signal — selecting it localizes the Google results. Add a city only if you want precise local results.
                  </p>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <h5 className="font-medium text-green-900 mb-2">How location works:</h5>
                    <ul className="space-y-1 text-green-800">
                      <li>• Country alone → a correct country-level SERP</li>
                      <li>• Add a city or area (e.g. "Madrid, Spain") → a precise local SERP</li>
                      <li>• The city is located automatically when you search — no separate step</li>
                      <li>• Host language sets the results and suggestions language</li>
                    </ul>
                  </div>
                </div>

                {/* Step 3 Details */}
                <div className="border-l-4 border-purple-500 pl-6">
                  <h4 className="text-xl font-semibold text-gray-900 mb-3">Step 3: Search</h4>
                  <p className="text-gray-600 mb-4">
                    Click Search to open the live Google SERP for your chosen location in a new tab. No VPN or proxy required.
                  </p>
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <h5 className="font-medium text-purple-900 mb-2">Good to know:</h5>
                    <ul className="space-y-1 text-purple-800">
                      <li>• The location is sent to Google via URL parameters (gl, hl, uule)</li>
                      <li>• Results open on google.com so you see something close to the page a signed-out local user gets</li>
                      <li>
                        • Want to know what those parameters contain?{' '}
                        <a href="/google-location-changer/" className="underline underline-offset-2 hover:text-purple-950">
                          See the uule, gl and hl guide with real example URLs
                        </a>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Call to Action */}
            <div className="text-center mt-12">
              <div className="brand-gradient rounded-2xl p-8 text-white">
                <h3 className="text-2xl font-bold mb-4">Ready to Check Your SERPs?</h3>
                <p className="text-blue-100 mb-6 max-w-2xl mx-auto">
                  Start analyzing Google search results from any location worldwide. 
                  Perfect for SEO professionals, marketers, and businesses targeting local markets.
                </p>
                <a 
                  href="/"
                  className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors inline-block"
                >
                  Start Using the Tool
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default HowToUsePage;
